import React, { useState, useEffect } from "react";
import useThemeHook from "../hooks/useThemeHook";
import { useTranslationHook } from "../hooks/useTranslationHook";
import { THEME_COLORS } from "../themes/DarkLight";
import Text from "./ThemedText";
import { Zap, Video } from "lucide-react";
import arthereumProvider from "../../backend/ArthereumProvider";
import { useDispatch, useSelector } from "react-redux";
import { actions } from "../redux";
import piNetworkAdsManager from "../utils/PiNetworkAdsManager";

const GrantNotAvailableCard = ({
  nextGrantDate = new Date(
    new Date().getTime() + 24 * 60 * 60 * 1000
  ).toDateString(),
  targetDate,
}: {
  nextGrantDate?: any;
  targetDate: any;
}) => {
  const dispatch = useDispatch();
  const userData = useSelector((state: any) => state?.actualUser);
  const user = userData?.user;
  const [loading, setLoading] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState("");
  const theme = useThemeHook();
  const getTranslationText = useTranslationHook();

  useEffect(() => {
    const calculateTimeRemaining = () => {
      const now = new Date().getTime();
      const target = new Date(targetDate).getTime();
      const difference = target - now;

      if (difference <= 0) {
        setTimeRemaining("Grant Available!");
        return;
      }

      const days = Math.floor(difference / (1000 * 60 * 60 * 24));
      const hours = Math.floor(
        (difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)
      );
      const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((difference % (1000 * 60)) / 1000);

      setTimeRemaining(`${days}:${hours}:${minutes}:${seconds}`);
    };

    const timer = setInterval(calculateTimeRemaining, 1000);
    calculateTimeRemaining();

    return () => clearInterval(timer);
  }, [targetDate]);

  const handleWatchAdToSkip = async () => {
    if (loading) return;
    
    setLoading(true);
    try {
      await piNetworkAdsManager.handleRewardedAdFlowFront(user.id, async () => {
        // Refill logic without charging Pi
        await arthereumProvider.instantRefill(user.id, 0); // Override backend method to charge 0
        
        // Update Redux locally
        dispatch(actions.setActualUser({
          ...userData,
          user: {
            ...user,
            lastMiningDate: null,
          },
          miningInfos: {
            ...userData.miningInfos,
            nextGrantDate: new Date(),
          },
        }));
        alert("Cooldown Skipped! You can claim your grant now! 🚀");
      });
    } catch (error: any) {
      console.error("Ad failed:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-transparent border border-gray-200 rounded-2xl p-6 shadow-sm hover:shadow-md transition-shadow">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className={`flex items-center justify-center w-10 h-10 rounded-full bg-${theme === "dark" ? "orange-500" : "amber-100"}`}>
            <span className={`text-lg font-semibold text-${theme === "dark" ? "white" : "amber-600"}`}>G</span>
          </div>

          <div>
            <p className={`text-sm text-${theme === "dark" ? "gray-300" : "gray-500"}`}>{getTranslationText("next_grant")}</p>
            <p className={`text-lg font-semibold text-${theme === "dark" ? "gray-100" : "gray-900"}`}>{nextGrantDate}</p>
          </div>
        </div>

        <p className={`text-base font-medium text-${theme === "dark" ? "orange-500" : "amber-600"}`}>{timeRemaining}</p>
      </div>
      
      <div className="mt-4 pt-4 border-t border-gray-100 flex items-center justify-between">
         <div className="flex items-center gap-2 text-xs text-gray-500">
            <Video size={14} className="text-amber-500" />
            <span>Don't want to wait?</span>
         </div>
         <button
           onClick={handleWatchAdToSkip}
           disabled={loading}
           className="bg-amber-100 hover:bg-amber-200 text-amber-700 px-4 py-2 rounded-xl text-xs font-bold transition-all active:scale-95 flex items-center gap-2"
         >
           {loading ? "Loading Ad..." : "Watch Ad to Skip Wait"}
         </button>
      </div>
    </div>
  );
};

export default GrantNotAvailableCard;
